import { useContext, useEffect, useState } from 'react';
import useAppQuery from 'hooks/useAppQuery';
import API from 'services/API';
import {
  Quote,
  QuoteOverrideFeeType,
  QuoteOverrides,
  QuoteResponse,
} from 'models/Quote';
import { useFormContext, useWatch } from 'react-hook-form';
import useAppUser from 'hooks/useAppUser';
import { getUnitType, isLeadAMainBooking } from 'utils/lead/leadUtils';
import { format } from 'date-fns';
import { HasOrderItems } from 'models/hasOrderItems/HasOrderItems';
import { prepareOrderItems } from 'utils/quote/quoteUtils';
import { FeeAmountTypeEnum, FeeScopeEnum } from 'models/PropertyFees';
import LeadModalContext from './LeadModalContext';
import useWatchQuoteGroupBookingData from './useWatchQuoteGroupBookingData';

// @ts-expect-error TS7006 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
const isQuoteNeedRefresh = (dirtyFields) => {
  if (dirtyFields?.quote) return true;
  if (dirtyFields?.propertyUid) return true;
  if (dirtyFields?.checkInDate) return true;
  if (dirtyFields?.checkOutDate) return true;
  if (dirtyFields?.adultCount) return true;
  if (dirtyFields?.childrenCount) return true;
  if (dirtyFields?.petCount) return true;
  if (dirtyFields?.force100PercentPaymentAtReservation) return true;
  return false;
};

const getQuoteOverridesValuesBasedOnOrder = (
  order: HasOrderItems,
  // @ts-expect-error TS7006 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
  dirtyFields,
): QuoteOverrides => {
  // @ts-expect-error TS2322 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
  if (!order) return null;

  if (dirtyFields && Object.keys(dirtyFields).length === 0) {
    // @ts-expect-error TS2322 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
    return null;
  }

  // If updating propertyUid we want to reset the whole quote
  if (dirtyFields?.propertyUid) {
    // @ts-expect-error TS2322 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
    return null;
  }

  // If updating guests or dates, we reset the rent and property fees
  const resetRentAndPropertyFeesAmount =
    dirtyFields?.checkInDate ||
    dirtyFields?.checkOutDate ||
    dirtyFields?.adultCount ||
    dirtyFields?.childrenCount ||
    dirtyFields?.petCount;

  const rentIsUpdated = dirtyFields?.quote?.rent?.rentBaseNetPrice;

  const overrides: QuoteOverrides = {
    // @ts-expect-error TS2322 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
    rent: !resetRentAndPropertyFeesAmount
      ? {
          rentBaseNetPrice:
            // @ts-expect-error TS18048 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
            order.rent?.rentBaseNetPrice > 0 ? order.rent?.rentBaseNetPrice : 0,
          extraGuestsBaseNetPrice:
            // @ts-expect-error TS18048 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
            order.rent?.extraGuestsBaseNetPrice > 0
              ? order.rent?.extraGuestsBaseNetPrice
              : 0,
          taxationRate: dirtyFields?.quote?.rent?.taxAmount
            ? null
            : order?.rent?.taxationRate,
          taxAmount:
            dirtyFields?.quote?.rent?.taxationRate || rentIsUpdated
              ? null
              : order?.rent?.taxAmount,
        }
      : {
          rentBaseNetPrice: null,
          extraGuestsBaseNetPrice: null,
          taxationRate:
            dirtyFields?.checkInDate || dirtyFields?.checkOutDate
              ? null
              : order?.rent?.taxationRate,
          taxAmount: null,
        },
    // @ts-expect-error TS2322 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
    securityDeposit: order.securityDeposit > 0 ? order.securityDeposit : 0,
    totalPrice: order.totalAmount > 0 ? order.totalAmount : 0,
    // @ts-expect-error TS2322 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
    force100PercentPaymentAtReservation:
      order.force100PercentPaymentAtReservation,
    fees: {
      cleaningFee: {
        ...order?.fees?.cleaningFee,
        // @ts-expect-error TS2322 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
        netPrice:
          // @ts-expect-error TS18048 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
          order?.fees?.cleaningFee?.netPrice > 0
            ? order?.fees?.cleaningFee?.netPrice
            : 0,
      },
      // @ts-expect-error TS2322 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
      otherFees: order?.fees?.otherFees.map((fee) => ({
        name: fee.name,
        taxationRate: fee.taxationRate,
        netPrice:
          (resetRentAndPropertyFeesAmount || rentIsUpdated) && fee.feeUid
            ? null
            : fee.netPrice,
        amount: null,
        amountType: FeeAmountTypeEnum.AMOUNT,
        scope: FeeScopeEnum.PER_STAY,
        type: QuoteOverrideFeeType.CUSTOM,
        isOptional: fee.isOptional,
        enabled: fee.enabled,
        feeId: fee.feeId,
        feeUid: fee.feeUid,
        removed: fee.removed,
      })),
    },
  };

  return overrides;
};

const useWatchQuoteData = () => {
  const {
    availableProperties,
    setOverrides,
    setPropertyFees,
    lead,
    setQuoteAdjustments,
  } = useContext(LeadModalContext);
  const { agencyUid } = useAppUser();
  const {
    reset,
    formState: { isDirty, dirtyFields },
    getValues,
  } = useFormContext();
  const [defaultQuote, setDefaultQuote] = useState<Quote | null>(null);
  const {
    quote,
    uid: leadUid,
    checkInDate,
    checkOutDate,
    adultCount,
    childrenCount,
    petCount,
    propertyUid,
  } = useWatch();

  const overridesAdjusted = getQuoteOverridesValuesBasedOnOrder(
    quote,
    dirtyFields,
  );

  const { groupBooking, isGroupBookingLoading } = useWatchQuoteGroupBookingData(
    { lead },
  );

  const { data: newQuote, isFetching: isNewQuoteLoading } = useAppQuery(
    [
      `get-quote-${leadUid}`,
      overridesAdjusted,
      leadUid,
      checkInDate,
      checkOutDate,
      adultCount,
      childrenCount,
      petCount,
      propertyUid,
    ],
    async () => {
      const unitType = getUnitType(availableProperties, propertyUid);
      const response = await API.post<QuoteResponse>('/api/v3/quotes', {
        leadUid: leadUid || undefined,
        overrides: overridesAdjusted,
        agencyUid,
        propertyUid: unitType?.uid || propertyUid,
        checkInDate: format(checkInDate, 'yyyy-MM-dd'),
        checkOutDate: format(checkOutDate, 'yyyy-MM-dd'),
        guests: parseInt(adultCount, 10) + parseInt(childrenCount, 10),
        petCount: parseInt(petCount, 10),
      });
      return response.data.quote;
    },
    {
      enabled:
        !!agencyUid &&
        !!propertyUid &&
        ((isDirty && isQuoteNeedRefresh(dirtyFields)) || !quote) &&
        // @ts-expect-error TS2345 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
        !isLeadAMainBooking(lead),
    },
  );

  useEffect(() => {
    if (!defaultQuote && newQuote) {
      setDefaultQuote(newQuote);
    }
  }, [newQuote]);

  useEffect(() => {
    if (newQuote) {
      // @ts-expect-error TS2345 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
      const newOrder = prepareOrderItems(newQuote, lead);
      const overrides = getQuoteOverridesValuesBasedOnOrder(quote, dirtyFields);

      setOverrides({ ...overrides });
      setPropertyFees(
        // @ts-expect-error TS18048 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
        newQuote.overrides.fees.otherFees.filter((fee) => fee.feeUid !== null),
      );

      setQuoteAdjustments(newQuote.adjustments);

      reset(
        {
          ...getValues(),
          propertyUid,
          checkInDate,
          checkOutDate,
          adultCount,
          childrenCount,
          petCount,
          quote: newOrder,
        },
        {
          keepDirty: false,
        },
      );
    }
  }, [newQuote]);

  return {
    defaultQuote,
    newQuote,
    isNewQuoteLoading,
    groupBooking,
    isGroupBookingLoading,
  };
};

export default useWatchQuoteData;
